import ContentBlock from "@/components/ContentBlock";
import ExpandableBlock from "@/components/ContentBlock/variants/ExpandableBlock";
import {
  DEFAULT_USE_IN_VIEW_OPTIONS,
  ElementType,
} from "@/constants/tagular/main";
import useElementEvents from "@/hooks/eventing/useElementEvents";
import parse from "html-react-parser";
import { useEffect, useState } from "react";
import { useInView } from "react-intersection-observer";
import useTranslations from "@/i18n/useTranslations";

export interface AuthorStatementTestimonial {
  textRaw: string;
  author: string;
}
export interface LearnerTestimonialsProps {
  testimonials: string | AuthorStatementTestimonial[];
  contentClassName?: string;
  containerBlockClassName?: string;
  trackEvents?: boolean;
}

export default function SideRailTestimonials({
  testimonials,
  contentClassName = "",
  trackEvents = false,
  containerBlockClassName = "p-4",
}: LearnerTestimonialsProps) {
  const tExpand = useTranslations("ExpandableBlock");
  const [isExpanded, setIsExpanded] = useState(false);
  const { elementViewed } = useElementEvents({
    webElement: {
      elementType: ElementType.Component,
      text: "Learner Testimonials",
      htmlId: "testimonials",
      name: "testimonials",
      location: "hero-side-rail",
      position: "1",
    },
  });

  const { ref: contentRef, inView } = useInView(DEFAULT_USE_IN_VIEW_OPTIONS);

  useEffect(() => {
    if (trackEvents && inView) {
      elementViewed();
    }
  }, [trackEvents, inView, elementViewed]);

  function styleTestimonials(html: string) {
    const paragraphs = html.match(/<p\b[^>]*>(.*?)<\/p>/g);
    if (paragraphs) {
      const lastParagraph = paragraphs[paragraphs.length - 1];

      for (let i = 0; i < paragraphs.length - 1; i++) {
        let p = paragraphs[i];
        p = p.replace(
          /-\s*([^<]+)\s*(?=<\/p>)/,
          (_m, author) =>
            `<span class="block mt-2 text-sm font-extrabold text-gray-600">— ${author.trim()}</span>`,
        );
        const modifiedParagraph = p.replace(
          "<p",
          `<p class="text-sm mb-1 text-gray-800"`,
        );
        html = html.replace(paragraphs[i], modifiedParagraph);
      }

      let last = lastParagraph;
      last = last.replace(
        /-\s*([^<]+)\s*(?=<\/p>)/,
        (_m, author) =>
          `<span class="block mt-2 text-sm font-extrabold text-gray-600">— ${author.trim()}</span>`,
      );
      const modifiedLastParagraph = last.replace(
        "<p",
        `<p class="text-sm text-gray-800 mb-0"`,
      );
      html = html.replace(lastParagraph, modifiedLastParagraph);
    }

    return html
      .replace(/<ul>/g, '<ul class="list-disc pl-4 text-base">')
      .replace(/<ol>/g, '<ol class="list-decimal pl-4 text-base">')
      .replace(/<li>/g, '<li class="ml-4 list-outside">')
      .replace(/<a /g, '<a class="text-info-500 hover:underline" ');
  }

  return (
    <div ref={contentRef}>
      <div className="flex flex-col bg-putty-100 border rounded-[8px] shadow w-full max-w-md mx-auto bg-white">
        {/* String testimonials */}
        {typeof testimonials === "string" &&
          (() => {
            const styledHtml = styleTestimonials(testimonials);
            const paragraphs =
              styledHtml.match(/<p\b[^>]*>(.*?)<\/p>/g)?.slice(0, 3) || [];
            const showToggle = paragraphs.length > 1;

            return (
              <>
                {paragraphs.map((paragraphHtml, idx) =>
                  !isExpanded && idx > 0 ? null : (
                    <ContentBlock
                      key={idx}
                      variant="quoted"
                      imageurl={""}
                      imagetitle={""}
                      className={containerBlockClassName}
                      width={16}
                      height={12}
                    >
                      <div
                        className="md:text-lg mb-0"
                        style={{ lineHeight: "1.925rem" }}
                      >
                        <ExpandableBlock
                          maxHeight={180}
                          btnClassName="text-[16px] font-normal mt-4 no-underline"
                        >
                          {parse(paragraphHtml)}
                        </ExpandableBlock>
                      </div>
                    </ContentBlock>
                  ),
                )}
                {showToggle && (
                  <button
                    className="flex flex-start underline cursor-pointer px-4 pb-4"
                    onClick={() => setIsExpanded(!isExpanded)}
                  >
                    {isExpanded ? tExpand("showLess") : tExpand("showMore")}
                  </button>
                )}
              </>
            );
          })()}

        {/* Array testimonials */}
        {Array.isArray(testimonials) && (
          <>
            {testimonials.map((testimonial, index) =>
              !isExpanded && index > 0 ? null : (
                <ContentBlock
                  key={index}
                  variant="quoted"
                  imageurl={""}
                  imagetitle={""}
                  className={containerBlockClassName}
                  width={16}
                  height={12}
                >
                  <div>
                    <p
                      className={`text-base mb-1 text-gray-800`}
                      style={{ lineHeight: "1.925rem" }}
                    >
                      <ExpandableBlock
                        maxHeight={20}
                        showMoreLabel="More"
                        btnClassName="text-[16px] font-normal mt-2 no-underline"
                        contentClassName={contentClassName}
                      >
                        {parse(testimonial.textRaw)}
                      </ExpandableBlock>
                    </p>
                    <p className="font-bold md:text-lg mb-0">
                      {testimonial.author}
                    </p>
                  </div>
                </ContentBlock>
              ),
            )}
            {testimonials.length > 1 && (
              <button
                className="underline cursor-pointer mb-2"
                onClick={() => setIsExpanded(!isExpanded)}
              >
                {isExpanded ? tExpand("showLess") : tExpand("showMore")}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
